import { EmploymentField, useEmploymentSelection } from './EmploymentField'
import { useEffect, useState } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'
import { useQuery } from '@tanstack/react-query'

import { useApp, type Localized } from '../../../i18n'
import { api } from '../../../api/client'
import { tone } from '../../../theme/tones'
import {
  Avatar, DateField, FormInput, FormTextArea, Icon, IconButton, OptionSelect,
} from '../../../ui'
import { todayYmd } from '../../../ui/DateField'
import { ymdPlusDays } from '../../../lib/date'
import {
  REC_ENUM_LABEL,
  REC_REASONS, REC_REPLACE_REASONS, REC_EXPERIENCE, REC_EDUCATION, REC_TEST_TYPES,
} from '../../../lib/edo-labels'
import type { RecruitmentInput } from '../../../lib/types'
import { useDepartments } from '../../users/useUsers'
import { useRecruitmentGrades, useCreateRecruitment } from '../useEdo'
import { recPatch, recruitmentMissing, type RecruitmentMissing } from '../recruitmentForm'
import { RoutePreview } from '../components/RouteTimeline'
import { Field, FormActions, MISSING_WORKPLACE } from './shared'

// ── Recruitment form (ТЗ-2026-011, полная анкета §6.2) ──────────────────────────
// Справочники (value = хранимое значение, label — локализуемая подпись) и подписи enum-кодов —
// в src/lib/edo-labels.ts, их же читает карточка заявки (CardDetails).
const REC_SCHEDULE: NonNullable<RecruitmentInput['workSchedule']>[] = ['5d', '6d', '2shift', '3shift']

/** Подписи пустых обязательных полей для подсказки под кнопкой — те же, что у самих полей. */
const MISSING_LABEL: Record<RecruitmentMissing, Localized> = {
  jobTitle: { ru: 'Название вакансии', en: 'Job title' },
  jobGrade: { ru: 'Уровень должности', en: 'Job grade' },
  department: { ru: 'Подразделение', en: 'Department' },
  workLocation: { ru: 'Фактическое место работы', en: 'Work location' },
  vacancyType: { ru: 'Тип вакансии', en: 'Vacancy type' },
  reasons: { ru: 'Причина открытия', en: 'Reason', az: 'Açılma səbəbi' },
  replacesName: { ru: 'Кого замещает (ФИО)', en: 'Who is replaced' },
  plannedStartDate: { ru: 'Дата выхода', en: 'Start date' },
  experience: { ru: 'Требуемый опыт', en: 'Experience' },
  education: { ru: 'Образование', en: 'Education' },
  techSkills: { ru: 'Технические компетенции', en: 'Tech skills', az: 'Texniki bacarıqlar' },
}

/**
 * Подчинение (ФИО рук.): свободный текст + подсказки из справочника людей
 * (debounce + /users?search — рецепт ManagerPicker/TripMatesPicker). Пик подставляет
 * ФИО в поле; DTO managerName остаётся строкой, ручной ввод по-прежнему допустим.
 */
function RecPersonSuggest({ value, onChange, width, placeholder }: {
  value: string
  onChange: (v: string) => void
  width?: number
  placeholder?: string
}) {
  const [picked, setPicked] = useState(false)
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value.trim()), 200)
    return () => clearTimeout(h)
  }, [value])
  const { data: hits } = useQuery({
    queryKey: ['users', debounced],
    queryFn: () => api.users(debounced),
    enabled: !picked && debounced.length >= 2,
  })
  const options = picked ? [] : (hits ?? []).slice(0, 6)
  return (
    <YStack width={width} gap={6}>
      <FormInput value={value} onChangeText={(v) => { setPicked(false); onChange(v) }} placeholder={placeholder} />
      {!picked && debounced.length >= 2 && options.length > 0 && (
        <YStack borderWidth={1} borderColor="$borderSecondary" borderRadius={7} overflow="hidden">
          {options.map((u, i) => (
            <XStack
              key={u.id}
              onPress={() => { onChange(u.fullName); setPicked(true) }}
              cursor="pointer"
              alignItems="center"
              gap={8}
              padding={8}
              borderTopWidth={i === 0 ? 0 : 1}
              borderColor="$borderSecondary"
              hoverStyle={{ backgroundColor: '$backgroundHover' }}
            >
              <Avatar name={u.fullName} seed={u.id} size="sm" />
              <YStack flex={1} minWidth={0}>
                <Text fontSize={13} color="$color" numberOfLines={1}>{u.fullName}</Text>
                {!!u.jobTitle && <Text fontSize={11} color="$muted" numberOfLines={1}>{u.jobTitle}</Text>}
              </YStack>
            </XStack>
          ))}
        </YStack>
      )}
    </YStack>
  )
}
function RecSection({ n, title }: { n: number; title: string }) {
  return (
    <XStack alignItems="center" gap={8} marginTop={8} marginBottom={-4}>
      <Stack width={22} height={22} borderRadius={999} backgroundColor="$primaryBg" alignItems="center" justifyContent="center">
        <Text fontSize={11} fontWeight="800" color="$primary">{n}</Text>
      </Stack>
      <Text fontSize={13} fontWeight="800" color="$color">{title}</Text>
    </XStack>
  )
}

export function RecruitmentForm({ onDone }: { onDone: () => void }) {
  const employment = useEmploymentSelection()
  const { t, lang } = useApp()
  const grades = useRecruitmentGrades()
  const deps = useDepartments()
  const create = useCreateRecruitment(onDone)
  const [f, setF] = useState<RecruitmentInput>({ jobTitle: '', jobGradeCode: '', department: '', currency: 'AZN' })
  // recPatch чистит парные поля при смене условия (скрытое поле не должно уехать в payload).
  const up = (patch: Partial<RecruitmentInput>) => setF((s) => recPatch(s, patch))

  const grade = grades.data?.find((g) => g.code === f.jobGradeCode)
  // Название грейда по локали (nameEn для EN; az — фолбэк AZ_DB по русскому названию).
  const gradeName = (g: { nameRu: string; nameEn: string }) => (lang === 'en' ? g.nameEn : t(g.nameRu))
  const needReplace = (f.reasons ?? []).some((r) => REC_REPLACE_REASONS.includes(r))
  const salaryOk = f.salaryFrom == null || f.salaryTo == null || f.salaryFrom <= f.salaryTo
  // «Завтра» в ЛОКАЛЬНОЙ TZ: toISOString() давал UTC-дату — в Баку (UTC+4) ночью это «сегодня».
  const tomorrow = ymdPlusDays(todayYmd(), 1)
  // Целое из ввода: берём первую цифровую группу («12.5» → 12, а не 125; «-5» → 5 — минус
  // в зарплате бессмысленен, бэк требует int ≥ 0). Пусто/не число → undefined.
  const num = (v: string) => { const m = v.match(/\d+/); return m ? Number(m[0]) : undefined }

  // Валидация по ТЗ (§6.5 / КП-01): пустые обязательные — все разом списком под кнопкой,
  // ошибки значения — красной строкой (их «заполните» не описывает).
  const missing = [
    ...(employment.ready ? [] : [t(MISSING_WORKPLACE)]),
    ...recruitmentMissing(f).map((k) => t(MISSING_LABEL[k])),
  ]
  const skills = f.techSkills?.trim() ?? ''
  const valueErr =
    !salaryOk ? t({ ru: 'Мин. зарплата больше максимальной', en: 'Min salary exceeds max' })
    : skills !== '' && skills.length < 50 ? t({ ru: `Технические компетенции — минимум 50 символов (сейчас ${skills.length})`, en: `Tech skills — min 50 chars (now ${skills.length})`, az: `Texniki bacarıqlar — ən azı 50 simvol (indi ${skills.length})` })
    : ''
  const ok = missing.length === 0 && valueErr === ''

  const submit = () => {
    if (!ok || create.isPending) return
    const langs = (f.languages ?? []).filter((x) => x.name.trim())
    const ivs = (f.interviewers ?? []).filter((x) => x.fullName.trim())
    create.mutate({
      employmentId: employment.employmentId,
      ...f,
      jobTitle: f.jobTitle.trim(),
      department: f.department.trim(),
      languages: langs.length ? langs : undefined,
      interviewers: ivs.length ? ivs : undefined,
      // Валюта имеет смысл только вместе с вилкой — без неё дефолтный AZN не шлём.
      currency: f.salaryFrom != null || f.salaryTo != null ? f.currency : undefined,
    })
  }

  return (
    <YStack gap={16}>
      <EmploymentField selection={employment} />
      {/* ── Секция 1: информация о вакансии ── */}
      <RecSection n={1} title={t({ ru: 'Информация о вакансии', en: 'Vacancy info' })} />
      <Field label={t({ ru: 'Название вакансии', en: 'Job title' })} required>
        <FormInput value={f.jobTitle} onChangeText={(v) => up({ jobTitle: v.slice(0, 120) })} placeholder="Data Analyst" />
      </Field>
      <Field label={t({ ru: 'Уровень должности', en: 'Job grade' })} required>
        <OptionSelect
          options={(grades.data ?? []).map((g) => ({ value: g.code, label: g.triggersCeo ? `${gradeName(g)}  ★${t({ ru: 'ГД', en: 'CEO' })}` : gradeName(g) }))}
          value={f.jobGradeCode ? [f.jobGradeCode] : []}
          onChange={(n) => up({ jobGradeCode: n[0] ?? '' })}
          placeholder={t({ ru: 'Выберите уровень', en: 'Select grade' })}
          highlight={!f.jobGradeCode}
        />
      </Field>
      {grade?.triggersCeo ? (
        <XStack alignItems="center" gap={7}>
          <Icon name="info" size={14} color={tone('gold').text} />
          <Text fontSize={12} fontWeight="600" color={tone('gold').text}>{t({ ru: 'В маршрут добавится согласование Ген. директора', en: 'CEO approval added to the route' })}</Text>
        </XStack>
      ) : null}
      <XStack gap={12} flexWrap="wrap">
        <Field label={t({ ru: 'Подразделение', en: 'Department' })} required>
          {/* Справочник /users/departments: точное значение важно — дашборд ОИ/HR фильтрует
              по equals. Пока список не загружен (или пуст) — свободный ввод, поле обязательное. */}
          {deps.data?.length ? (
            <OptionSelect
              options={deps.data.map((d) => d.name)}
              value={f.department ? [f.department] : []}
              onChange={(n) => up({ department: n[0] ?? '' })}
              placeholder={t({ ru: 'Выберите подразделение', en: 'Select department' })}
              highlight={!f.department}
            />
          ) : (
            <FormInput width={220} value={f.department} onChangeText={(v) => up({ department: v.slice(0, 200) })} placeholder="IT-департамент" />
          )}
        </Field>
        <Field label={t({ ru: 'Подчинение (ФИО рук.)', en: 'Reports to' })}>
          <RecPersonSuggest
            width={220}
            value={f.managerName ?? ''}
            onChange={(v) => up({ managerName: v.slice(0, 200) })}
            placeholder={t({ ru: 'Начните вводить имя…', en: 'Type a name…' })}
          />
        </Field>
      </XStack>
      <XStack gap={12} flexWrap="wrap">
        <Field label={t({ ru: 'Проект (при наличии)', en: 'Project' })}>
          <FormInput width={220} value={f.project ?? ''} onChangeText={(v) => up({ project: v.slice(0, 200) })} placeholder="—" />
        </Field>
        <Field label={t({ ru: 'Фактическое место работы', en: 'Work location' })} required>
          <FormInput width={220} value={f.workLocation ?? ''} onChangeText={(v) => up({ workLocation: v.slice(0, 200) })} placeholder={t({ ru: 'Офис / локация', en: 'Office / location' })} />
        </Field>
      </XStack>
      <Field label={t({ ru: 'Режим работы', en: 'Work mode' })}>
        <XStack gap={12} flexWrap="wrap" alignItems="center">
          <OptionSelect
            options={(['full', 'part'] as const).map((v) => ({ value: v, label: t(REC_ENUM_LABEL[v]) }))}
            value={f.employmentRate ? [f.employmentRate] : []}
            onChange={(n) => up({ employmentRate: n[0] as RecruitmentInput['employmentRate'] })}
          />
          <OptionSelect
            options={REC_SCHEDULE.map((v) => ({ value: v, label: t(REC_ENUM_LABEL[v]) }))}
            value={f.workSchedule ? [f.workSchedule] : []}
            onChange={(n) => up({ workSchedule: n[0] as RecruitmentInput['workSchedule'] })}
            placeholder={t({ ru: 'График', en: 'Schedule' })}
          />
        </XStack>
      </Field>
      <Field label={t({ ru: 'Примечание', en: 'Note' })}>
        <FormInput value={f.note ?? ''} onChangeText={(v) => up({ note: v.slice(0, 1000) })} placeholder="—" />
      </Field>

      {/* ── Секция 2: обоснование и бюджет ── */}
      <RecSection n={2} title={t({ ru: 'Обоснование и бюджет', en: 'Justification & budget' })} />
      <Field label={t({ ru: 'Тип вакансии', en: 'Vacancy type' })} required>
        <OptionSelect
          options={(['new', 'budgeted'] as const).map((v) => ({ value: v, label: t(REC_ENUM_LABEL[v]) }))}
          value={f.vacancyType ? [f.vacancyType] : []}
          onChange={(n) => up({ vacancyType: n[0] as RecruitmentInput['vacancyType'] })}
        />
      </Field>
      {f.vacancyType === 'new' ? (
        <XStack gap={12} flexWrap="wrap" alignItems="flex-end">
          <Field label={t({ ru: 'Вид штатной единицы', en: 'Unit type' })}>
            <OptionSelect
              options={(['intern', 'temporary', 'project'] as const).map((v) => ({ value: v, label: t(REC_ENUM_LABEL[v]) }))}
              value={f.staffUnitType ? [f.staffUnitType] : []}
              onChange={(n) => up({ staffUnitType: n[0] as RecruitmentInput['staffUnitType'] })}
            />
          </Field>
          {f.staffUnitType === 'temporary' || f.staffUnitType === 'project' ? (
            <Field label={t({ ru: 'Срок действия', en: 'Term' })}>
              <FormInput width={160} value={f.termOfValidity ?? ''} onChangeText={(v) => up({ termOfValidity: v.slice(0, 100) })} placeholder={t({ ru: 'напр. 6 мес.', en: 'e.g. 6 mo.' })} />
            </Field>
          ) : null}
        </XStack>
      ) : null}
      <Field label={t({ ru: 'Причина открытия (можно несколько)', en: 'Reason(s)' })} required>
        <OptionSelect
          options={REC_REASONS.map((o) => ({ value: o.value, label: t(o.label) }))}
          value={f.reasons ?? []}
          onChange={(n) => up({ reasons: n })}
          multiple
          placeholder={t({ ru: 'Выберите причину', en: 'Select reason' })}
          highlight={!f.reasons?.length}
        />
      </Field>
      {needReplace ? (
        <Field label={t({ ru: 'Кого замещает (ФИО)', en: 'Who is replaced' })} required>
          <FormInput value={f.replacesName ?? ''} onChangeText={(v) => up({ replacesName: v.slice(0, 200) })} placeholder="ФИО" />
        </Field>
      ) : null}
      <XStack gap={12} flexWrap="wrap" alignItems="flex-end">
        <Field label={t({ ru: 'Зарплата от', en: 'Salary from' })}>
          <FormInput width={110} keyboardType="numeric" value={f.salaryFrom != null ? String(f.salaryFrom) : ''} onChangeText={(v) => up({ salaryFrom: num(v) })} placeholder="—" borderColor={!salaryOk ? tone('red').solid : '$borderColor'} />
        </Field>
        <Field label={t({ ru: 'до', en: 'to' })}>
          <FormInput width={110} keyboardType="numeric" value={f.salaryTo != null ? String(f.salaryTo) : ''} onChangeText={(v) => up({ salaryTo: num(v) })} placeholder="—" borderColor={!salaryOk ? tone('red').solid : '$borderColor'} />
        </Field>
        <Field label={t({ ru: 'Валюта', en: 'Currency' })}>
          <OptionSelect
            options={['AZN', 'USD', 'EUR']}
            value={f.currency ? [f.currency] : []}
            onChange={(n) => up({ currency: n[0] as RecruitmentInput['currency'] })}
          />
        </Field>
      </XStack>
      {/* Отдельной строкой, у левого края: в ряду зарплаты поле попадало к правому краю, и календарь
          (раскрывается вправо) на 375 уводил карточку в горизонтальный скролл. */}
      <Field label={t({ ru: 'Дата выхода', en: 'Start date' })} required>
        <DateField value={f.plannedStartDate ?? ''} onChange={(v) => up({ plannedStartDate: v })} min={tomorrow} placeholder={t({ ru: 'Дата', en: 'Date' })} highlight={!f.plannedStartDate} />
      </Field>
      <Field label={t({ ru: 'Иные причины', en: 'Other reasons' })}>
        <FormInput value={f.otherReasons ?? ''} onChangeText={(v) => up({ otherReasons: v.slice(0, 1000) })} placeholder="—" />
      </Field>

      {/* ── Секция 3: требования к кандидату ── */}
      <RecSection n={3} title={t({ ru: 'Требования к кандидату', en: 'Candidate requirements' })} />
      <XStack gap={12} flexWrap="wrap">
        <Field label={t({ ru: 'Возрастные ограничения', en: 'Age limits' })}>
          <FormInput width={200} value={f.ageLimit ?? ''} onChangeText={(v) => up({ ageLimit: v.slice(0, 100) })} placeholder={t({ ru: 'без ограничений', en: 'none' })} />
        </Field>
      </XStack>
      <Field label={t({ ru: 'Требуемый опыт', en: 'Experience' })} required>
        <OptionSelect
          options={REC_EXPERIENCE.map((o) => ({ value: o.value, label: t(o.label) }))}
          value={f.experience ? [f.experience] : []}
          onChange={(n) => up({ experience: n[0] })}
          placeholder={t({ ru: 'Выберите опыт', en: 'Select experience' })}
          highlight={!f.experience}
        />
      </Field>
      <Field label={t({ ru: 'Образование', en: 'Education' })} required>
        <OptionSelect
          options={REC_EDUCATION.map((o) => ({ value: o.value, label: t(o.label) }))}
          value={f.education ? [f.education] : []}
          onChange={(n) => up({ education: n[0] })}
          placeholder={t({ ru: 'Выберите образование', en: 'Select education' })}
          highlight={!f.education}
        />
      </Field>
      <Field label={t({ ru: 'Обучение / сертификаты', en: 'Certificates' })}>
        <FormInput value={f.certificates ?? ''} onChangeText={(v) => up({ certificates: v.slice(0, 500) })} placeholder="—" />
      </Field>
      {/* Языки (мульти-строки) */}
      <Field label={t({ ru: 'Знание языков', en: 'Languages' })}>
        <YStack gap={8}>
          {(f.languages ?? []).map((lng, i) => (
            <XStack key={i} gap={8} alignItems="center">
              {/* Лимиты DTO (60/20) бэк внутри массива НЕ проверяет (нет @ValidateNested) — фронт единственная защита. */}
              <FormInput flex={1} value={lng.name} onChangeText={(v) => { const a = [...(f.languages ?? [])]; a[i] = { ...a[i], name: v.slice(0, 60) }; up({ languages: a }) }} placeholder={t({ ru: 'Язык', en: 'Language' })} />
              <FormInput width={110} value={lng.level ?? ''} onChangeText={(v) => { const a = [...(f.languages ?? [])]; a[i] = { ...a[i], level: v.slice(0, 20) }; up({ languages: a }) }} placeholder="A1–C2" />
              <IconButton onPress={() => up({ languages: (f.languages ?? []).filter((_, j) => j !== i) })}><Icon name="x" size={15} color="$muted" /></IconButton>
            </XStack>
          ))}
          <XStack onPress={() => up({ languages: [...(f.languages ?? []), { name: '' }] })} cursor="pointer" alignItems="center" gap={6}>
            <Icon name="plus" size={14} color="$primary" />
            <Text fontSize={12} fontWeight="600" color="$primary">{t({ ru: 'Добавить язык', en: 'Add language' })}</Text>
          </XStack>
        </YStack>
      </Field>
      <Field label={t({ ru: 'Технические компетенции (мин. 50 символов)', en: 'Tech skills (min 50 chars)' })} required>
        <FormTextArea value={f.techSkills ?? ''} onChangeText={(v) => up({ techSkills: v })} maxLength={2000}
          placeholder={t({ ru: 'Ключевые навыки, ПО, инструменты…', en: 'Key skills, software, tools…' })} />
      </Field>
      <Field label={t({ ru: 'Личностные качества', en: 'Personal qualities' })}>
        <FormInput value={f.personalQualities ?? ''} onChangeText={(v) => up({ personalQualities: v.slice(0, 1000) })} placeholder="—" />
      </Field>

      {/* ── Секция 4: дополнительные условия ── */}
      <RecSection n={4} title={t({ ru: 'Дополнительные условия', en: 'Extra conditions' })} />
      <OptionSelect
        multiple
        options={[
          { value: 'travelDomestic', label: t({ ru: 'Командировки по стране', en: 'Domestic travel' }) },
          { value: 'travelInternational', label: t({ ru: 'Командировки за рубеж', en: 'Intl travel' }) },
          { value: 'ownCar', label: t({ ru: 'Личный автомобиль', en: 'Own car' }) },
          { value: 'driverLicense', label: t({ ru: 'Водительское удостоверение', en: 'Driver license' }) },
        ]}
        value={[
          ...(f.travelDomestic ? ['travelDomestic'] : []),
          ...(f.travelInternational ? ['travelInternational'] : []),
          ...(f.ownCar ? ['ownCar'] : []),
          ...(f.driverLicense ? ['driverLicense'] : []),
        ]}
        onChange={(n) =>
          up({
            travelDomestic: n.includes('travelDomestic'),
            travelInternational: n.includes('travelInternational'),
            ownCar: n.includes('ownCar'),
            driverLicense: n.includes('driverLicense'),
          })
        }
        placeholder={t({ ru: 'Выберите условия', en: 'Select conditions' })}
      />
      {f.driverLicense ? (
        <Field label={t({ ru: 'Категория ВУ', en: 'License category' })}>
          <FormInput width={160} value={f.driverCategory ?? ''} onChangeText={(v) => up({ driverCategory: v.slice(0, 50) })} placeholder="B, C…" />
        </Field>
      ) : null}
      <Field label={t({ ru: 'Иные условия', en: 'Other conditions' })}>
        <FormInput value={f.otherConditions ?? ''} onChangeText={(v) => up({ otherConditions: v.slice(0, 1000) })} placeholder="—" />
      </Field>

      {/* ── Секция 5: проверяемые навыки ── */}
      <RecSection n={5} title={t({ ru: 'Проверяемые знания и навыки', en: 'Skills to assess' })} />
      <OptionSelect
        multiple
        options={[{ value: 'on', label: t({ ru: 'Требуется тестирование', en: 'Testing required' }) }]}
        value={f.testingRequired ? ['on'] : []}
        onChange={(n) => up({ testingRequired: n.length > 0 })}
      />
      {f.testingRequired ? (
        <Field label={t({ ru: 'Виды проверки', en: 'Test types' })}>
          <OptionSelect
            options={REC_TEST_TYPES.map((o) => ({ value: o.value, label: t(o.label) }))}
            value={f.testTypes ?? []}
            onChange={(n) => up({ testTypes: n })}
            multiple
            placeholder={t({ ru: 'Выберите виды', en: 'Select types' })}
          />
        </Field>
      ) : null}
      <Field label={t({ ru: 'Иные проверяемые навыки', en: 'Other skills to assess' })}>
        <FormInput value={f.otherSkills ?? ''} onChangeText={(v) => up({ otherSkills: v.slice(0, 500) })} placeholder="—" />
      </Field>

      {/* ── Секция 6: участники собеседований ── */}
      <RecSection n={6} title={t({ ru: 'Участники собеседований', en: 'Interview panel' })} />
      <YStack gap={8}>
        {(f.interviewers ?? []).map((iv, i) => (
          <XStack key={i} gap={8} alignItems="center">
            {/* Лимиты DTO (200/200) бэк внутри массива НЕ проверяет — режем на вводе. */}
            <FormInput flex={1} value={iv.fullName} onChangeText={(v) => { const a = [...(f.interviewers ?? [])]; a[i] = { ...a[i], fullName: v.slice(0, 200) }; up({ interviewers: a }) }} placeholder={t({ ru: 'ФИО', en: 'Full name' })} />
            <FormInput flex={1} value={iv.position ?? ''} onChangeText={(v) => { const a = [...(f.interviewers ?? [])]; a[i] = { ...a[i], position: v.slice(0, 200) }; up({ interviewers: a }) }} placeholder={t({ ru: 'Должность', en: 'Position' })} />
            <IconButton onPress={() => up({ interviewers: (f.interviewers ?? []).filter((_, j) => j !== i) })}><Icon name="x" size={15} color="$muted" /></IconButton>
          </XStack>
        ))}
        {(f.interviewers ?? []).length < 10 ? (
          <XStack onPress={() => up({ interviewers: [...(f.interviewers ?? []), { fullName: '' }] })} cursor="pointer" alignItems="center" gap={6}>
            <Icon name="plus" size={14} color="$primary" />
            <Text fontSize={12} fontWeight="600" color="$primary">{t({ ru: 'Добавить участника', en: 'Add member' })}</Text>
          </XStack>
        ) : null}
      </YStack>

      {/* Общее: свободнотекстовая причина (поле DTO `reason`; отличается от справочника reasons[]) + комментарий */}
      <Field label={t({ ru: 'Причина открытия (свободный текст)', en: 'Reason (free text)' })}>
        <FormInput value={f.reason ?? ''} onChangeText={(v) => up({ reason: v.slice(0, 1000) })} placeholder="—" />
      </Field>
      <Field label={t({ ru: 'Комментарий (необязательно)', en: 'Comment (optional)' })}>
        <FormTextArea value={f.comment ?? ''} onChangeText={(v) => up({ comment: v })} maxLength={1000}
          placeholder={t({ ru: 'Дополнения к заявке…', en: 'Additional notes…' })} minHeight={52} />
      </Field>

      {valueErr !== '' && <Text fontSize={12} color={tone('red').text}>{valueErr}</Text>}

      <RoutePreview type="RECRUITMENT" grade={f.jobGradeCode || undefined} employmentId={employment.employmentId} enabled={employment.ready} />
      <FormActions onCancel={onDone} onSubmit={submit} ok={ok} m={create} missing={missing} />
    </YStack>
  )
}
